/*
 * Defines interfaces specific to UCRM. Members shared with TPRM are defined in `Models/Organization.ts`, `Models/AssessmentSubject.ts`, `Models/Folders.ts`, `Models/InherentRisk.ts`, `Models/DueDiligence.ts`, `Models/DueDiligenceWorkflow.ts`, and `Models/RiskRating.ts`.
 */
import { AssessmentSubjectBase, AssessmentSubjectForNonPrivilegedUserBase, ModuleEntities, OrganizationWithAssessmentSubjectsForNonPrivilegedUserBase, UpdateAssessmentSubjectRequestBase } from './AssessmentSubject';
import { AssessmentSelections, DueDiligenceAssessmentBase, UpdateDueDiligenceAssessmentAssignmentsRequest } from './DueDiligence';
import { CompleteIntakeRequestBase, DueDiligenceWorkflowArchiveBase } from './DueDiligenceWorkflow';
import { SaveOrSubmitInherentRiskQuestionnaireRequestBase, SubmitInherentRiskQuestionnaireSelectionsBase } from './InherentRisk';
import { CreateOrganizationRequestBase, FinalizationState, OrganizationBase, OrganizationForNonPrivilegedUserBase, OrganizationResponseBase, UpdateOrganizationRequestBase } from './Organization';
import { DueDiligenceModule, ProductModule } from './ProductModule';
import { AssociatedThirdPartyService } from './TPRM';
import { DisplayableTag } from './Tags';

/** The UCRM counterpart of a TPRM `ThirdParty`. */
export interface Business extends OrganizationBase {
    created_by: string;
}

export type BusinessArchive = Omit<Business, 'tags'> & { tags: DisplayableTag[] };

export interface BusinessResponse extends OrganizationResponseBase {
    created_by: string;
}

export interface BusinessResponseWithUseCases extends BusinessResponse {
    use_cases: UseCase[];
}

export interface BusinessForNonUcrmUser extends OrganizationForNonPrivilegedUserBase {
    created_by: string;
}

export interface BusinessWithUseCasesForNonUcrmUser extends OrganizationWithAssessmentSubjectsForNonPrivilegedUserBase {
    created_by: string;
    use_cases: UseCaseForNonUcrmUser[];
}

/** Used to create a business. */
export type CreateBusinessRequest = CreateOrganizationRequestBase;

/** Used to update a business. Only the properties that have changed are included. */
export type UpdateBusinessRequest = UpdateOrganizationRequestBase;

/** The UCRM counterpart of a TPRM `ThirdPartyService`. */
export interface UseCase extends AssessmentSubjectBase {
    associated_tprm_services: AssociatedThirdPartyService[];
    created_by: string;
}

export type UseCaseArchive = Omit<UseCase, 'tags'> & { tags: DisplayableTag[] };

export interface UseCaseForNonUcrmUser extends AssessmentSubjectForNonPrivilegedUserBase {
    created_by: string;
}

/** Used to update a use case. See the warning on `UpdateAssessmentSubjectRequestBase`. */
export interface UpdateUseCaseRequest extends UpdateAssessmentSubjectRequestBase {
    associated_tprm_services?: string[]; // The IDs of the TPRM third-party services to associate with the use case, replacing the current associations. If omitted, the current associations are left unchanged (unlike the members of `UpdateAssessmentSubjectRequestBase`). When creating a use case, omitting this is the same as providing an empty list.
}

/** Used to create a use case. */
export interface CreateUseCaseRequest extends UpdateUseCaseRequest {
    finalization_state: FinalizationState;
}

export interface UcrmAssessmentSelections extends AssessmentSelections {
    questionnaire_respondent_ids?: string[]; // The users who will answer the questionnaire. If omitted or empty, the use case's manager and delegates are used.
}

export interface UcrmSubmitInherentRiskQuestionnaireSelections extends SubmitInherentRiskQuestionnaireSelectionsBase {
    assessment_selections: UcrmAssessmentSelections[]; // The list of assessments to generate for the use case.
}

export interface UcrmSaveOrSubmitInherentRiskQuestionnaireRequest extends SaveOrSubmitInherentRiskQuestionnaireRequestBase {
    submit_selections: UcrmSubmitInherentRiskQuestionnaireSelections; // Ignored if `submit` is false.
}

export interface UcrmUpdateDueDiligenceAssessmentAssignmentsRequest extends UpdateDueDiligenceAssessmentAssignmentsRequest {
    questionnaire_respondent_ids?: string[]; // If omitted or empty, the use case's manager and delegates are used.
}

/** A UCRM due diligence assessment, whose questionnaire is completed by SummIT users (respondents) rather than third-party contacts. */
export interface UcrmDueDiligenceAssessment extends DueDiligenceAssessmentBase {
    questionnaire_respondent_ids: string[]; // The users (in addition to admins) who are allowed to answer and submit the questionnaire.

    questionnaire_submitted_by?: string; // The ID of the user who submitted the questionnaire.
}

/** A snapshot of a use case, taken when its due diligence workflow is completed. */
export interface UcrmDueDiligenceWorkflowArchive extends DueDiligenceWorkflowArchiveBase {
    assessments: UcrmDueDiligenceAssessment[];
    business: BusinessArchive;
    use_case: UseCaseArchive;
}

export const DEFAULT_UCRM_INTAKE_INSTRUCTIONS = 'Create a new business and/or use case, and optionally complete the inherent risk questionnaire. Items are saved as drafts until the Submit step is completed.';

export interface CompleteUcrmIntakeRequest extends CompleteIntakeRequestBase {
    submit_irq_selections: UcrmSubmitInherentRiskQuestionnaireSelections; // Ignored if `submit_irq` is false.
}

/** Returns the users who are allowed to answer the questionnaire of an assessment. Only UCRM assessments have respondents; TPRM questionnaires are answered by third-party contacts, so the result is empty for TPRM assessments. */
export const getQuestionnaireRespondentIds = (module: DueDiligenceModule, assessment: DueDiligenceAssessmentBase | UcrmDueDiligenceAssessment): string[] => (module === ProductModule.UCRM ? (assessment as UcrmDueDiligenceAssessment).questionnaire_respondent_ids : []);

/** The UCRM entity types, for the UI that is shared by TPRM and UCRM. See `ModuleEntities`. */
export interface UcrmEntities extends ModuleEntities {
    organization: BusinessResponse;
    organizationForNonPrivilegedUser: BusinessForNonUcrmUser;
    organizationWithSubjects: BusinessResponseWithUseCases;
    organizationWithSubjectsForNonPrivilegedUser: BusinessWithUseCasesForNonUcrmUser;
    subject: UseCase;
    subjectForNonPrivilegedUser: UseCaseForNonUcrmUser;
}
